/**
 * Portrait camera with centroid follow.
 * Portrait 9:16-first (modest FOV, behind-and-above squad); landscape/iPad
 * widens FOV so the bridge stays readable without letterboxing.
 */
import * as THREE from "three";
const DEFAULTS = {
    /** Modest FOV — bridge vanishes at horizon (ad 9:16). */
    fov: 46,
    /** Wider vertical FOV on landscape so deck + rails stay readable. */
    landscapeFov: 54,
    back: 12,
    height: 8.5,
    lookHeight: 1.15,
    lookAhead: 14,
    followHz: 8,
    near: 0.1,
    far: 450,
};
/**
 * Portrait-first FOV: modest at 9:16, gently wider as the canvas widens
 * (iPad / landscape). No letterboxing — full canvas, FOV/framing only.
 */
export function portraitFovForAspect(aspect, options = {}) {
    const portraitFov = options.fov ?? DEFAULTS.fov;
    const landscapeFov = options.landscapeFov ?? DEFAULTS.landscapeFov;
    const a = Math.max(0.01, aspect);
    // 9:16 ≈ 0.5625; square = 1; 16:9 ≈ 1.78
    const portraitAspect = 9 / 16;
    const landscapeAspect = 16 / 9;
    if (a <= portraitAspect)
        return portraitFov;
    if (a >= landscapeAspect)
        return landscapeFov;
    const t = (a - portraitAspect) / (landscapeAspect - portraitAspect);
    return portraitFov + t * (landscapeFov - portraitFov);
}
/**
 * Create a perspective camera configured for portrait runner presentation.
 */
export function createPortraitCamera(aspect, options = {}) {
    const a = Math.max(aspect, 0.01);
    const fov = portraitFovForAspect(a, options);
    const near = options.near ?? DEFAULTS.near;
    const far = options.far ?? DEFAULTS.far;
    const camera = new THREE.PerspectiveCamera(fov, a, near, far);
    camera.name = "portraitCamera";
    // Behind-and-above squad, looking down the bridge toward the horizon
    const back = options.back ?? DEFAULTS.back;
    const height = options.height ?? DEFAULTS.height;
    const lookHeight = options.lookHeight ?? DEFAULTS.lookHeight;
    const lookAhead = options.lookAhead ?? DEFAULTS.lookAhead;
    camera.position.set(0, height, -back);
    camera.lookAt(0, lookHeight, lookAhead);
    return camera;
}
/**
 * Smoothly follow a target centroid. Call once per display frame with dt seconds.
 * Camera sits behind (-Z relative to path forward) and above the squad;
 * look-at is pushed ahead so the deck vanishes into fog at the horizon.
 */
export function updateCentroidFollow(camera, follow, target, dt, options = {}) {
    const back = options.back ?? DEFAULTS.back;
    const height = options.height ?? DEFAULTS.height;
    const lookHeight = options.lookHeight ?? DEFAULTS.lookHeight;
    const lookAhead = options.lookAhead ?? DEFAULTS.lookAhead;
    const followHz = options.followHz ?? DEFAULTS.followHz;
    const alpha = 1 - Math.exp(-followHz * Math.max(0, dt));
    follow.x += (target.x - follow.x) * alpha;
    follow.y += (target.y - follow.y) * alpha;
    follow.z += (target.z - follow.z) * alpha;
    camera.position.set(follow.x, follow.y + height, follow.z - back);
    camera.lookAt(follow.x, follow.y + lookHeight, follow.z + lookAhead);
}
/** Snap follow state (and camera) immediately to target — use on run start. */
export function snapCentroidFollow(camera, follow, target, options = {}) {
    follow.x = target.x;
    follow.y = target.y;
    follow.z = target.z;
    const back = options.back ?? DEFAULTS.back;
    const height = options.height ?? DEFAULTS.height;
    const lookHeight = options.lookHeight ?? DEFAULTS.lookHeight;
    const lookAhead = options.lookAhead ?? DEFAULTS.lookAhead;
    camera.position.set(follow.x, follow.y + height, follow.z - back);
    camera.lookAt(follow.x, follow.y + lookHeight, follow.z + lookAhead);
}
/**
 * Update camera aspect for portrait-first layouts and adapt FOV for landscape.
 * Call on canvas resize; no letterboxing.
 */
export function setPortraitAspect(camera, width, height, options = {}) {
    const w = Math.max(1, width);
    const h = Math.max(1, height);
    const aspect = w / h;
    camera.aspect = aspect;
    // Only overwrite FOV when not mid juice-punch (caller may restore base later).
    // Always set the aspect-adapted "base" FOV; juice layer re-applies on top each frame.
    camera.fov = portraitFovForAspect(aspect, options);
    camera.updateProjectionMatrix();
}
/** True when canvas is taller than wide (mobile portrait). */
export function isPortraitAspect(width, height) {
    return height >= width;
}
/** Default portrait FOV (for juice base / tests). */
export function defaultPortraitFov() {
    return DEFAULTS.fov;
}
//# sourceMappingURL=camera.js.map